/**
 * Planner Configuration Service
 *
 * Loads wizard config from database, falls back to JSON file.
 * Saves create new versions (version history in database).
 */

import { supabase } from '../lib/supabase';
import questionsConfig from '../config/modelPlannerQuestions.json';

let configCache = null;

// Exported for use in UI components
export const NAV_TOKENS = [
  'SECTION_COMPLETE',
  'LOOP_START_SINGLE',
  'LOOP_CONTINUE',
  'LOOP_END',
  'ADA_LOOP_START_SINGLE',
  'ADA_LOOP_CONTINUE',
  'ADA_LOOP_END'
];

export class PlannerConfigService {

  /**
   * Load active configuration from database, fallback to JSON
   */
  static async loadConfiguration() {
    if (configCache) {
      return configCache;
    }

    try {
      const { data, error } = await supabase
        .from('planner_wizard_config')
        .select('config_data, version_id')
        .eq('is_active', true)
        .single();

      if (data?.config_data) {
        configCache = {
          ...data.config_data,
          _versionId: data.version_id,
          _fromDatabase: true
        };
        return configCache;
      }

      // No active config - seed from JSON
      if (error?.code === 'PGRST116') {
        await this.seedFromJson();
        const { data: seeded } = await supabase
          .from('planner_wizard_config')
          .select('config_data, version_id')
          .eq('is_active', true)
          .single();

        if (seeded?.config_data) {
          configCache = {
            ...seeded.config_data,
            _versionId: seeded.version_id,
            _fromDatabase: true
          };
          return configCache;
        }
      }
    } catch (err) {
      console.warn('[PlannerConfig] Database error, using JSON fallback:', err.message);
    }

    // Fallback to JSON file
    configCache = {
      sections: questionsConfig.sections,
      _fromDatabase: false
    };
    return configCache;
  }

  /**
   * Seed database from JSON file (only if empty)
   */
  static async seedFromJson() {
    try {
      const { data: existing } = await supabase
        .from('planner_wizard_config')
        .select('id')
        .limit(1);

      if (!existing || existing.length === 0) {
        await supabase
          .from('planner_wizard_config')
          .insert({
            config_data: { sections: questionsConfig.sections },
            is_active: true,
            change_description: 'Initial seed from JSON'
          });
      }
    } catch (err) {
      console.error('[PlannerConfig] Error seeding:', err);
    }
  }

  /**
   * Clear cache
   */
  static invalidateCache() {
    configCache = null;
  }

  /**
   * Save configuration atomically using database function
   * Creates new version and sets as active in single transaction
   */
  static async saveConfiguration(configData, userId, changeDescription = '') {
    const errors = this.validateConfig(configData);
    if (errors.length > 0) {
      return { data: null, error: { message: 'Validation failed', validationErrors: errors } };
    }

    try {
      // Use RPC for atomic save (function handles deactivate + insert in single transaction)
      const { data, error } = await supabase
        .rpc('save_planner_config_version', {
          p_config_data: configData,
          p_created_by: userId,
          p_change_description: changeDescription
        })
        .single();

      if (error) {
        // Fallback to non-atomic save if function doesn't exist yet
        if (error.code === '42883') {
          console.warn('[PlannerConfig] RPC function not found, using fallback save');
          return this.saveConfigurationFallback(configData, userId, changeDescription);
        }
        return { data: null, error };
      }

      configCache = { ...configData, _versionId: data.version_id, _fromDatabase: true };
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  /**
   * Fallback save method (non-atomic, for when RPC function isn't available)
   */
  static async saveConfigurationFallback(configData, userId, changeDescription = '') {
    try {
      // Deactivate current
      await supabase
        .from('planner_wizard_config')
        .update({ is_active: false })
        .eq('is_active', true);

      // Insert new active version
      const { data, error } = await supabase
        .from('planner_wizard_config')
        .insert({
          config_data: configData,
          is_active: true,
          created_by: userId,
          change_description: changeDescription
        })
        .select()
        .single();

      if (error) {
        return { data: null, error };
      }

      configCache = { ...configData, _versionId: data.version_id, _fromDatabase: true };
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  /**
   * Validate configuration - checks navigation references
   */
  static validateConfig(config) {
    const errors = [];

    if (!config?.sections || typeof config.sections !== 'object') {
      errors.push({ type: 'invalid_structure', message: 'Configuration must have a "sections" object' });
      return errors;
    }

    // Collect all question IDs
    const allQuestionIds = new Set();
    Object.values(config.sections).forEach(section => {
      if (section?.questions) {
        Object.keys(section.questions).forEach(qId => allQuestionIds.add(qId));
      }
    });

    // Validate each section
    Object.entries(config.sections).forEach(([sectionKey, section]) => {
      if (!section) {
        errors.push({ section: sectionKey, message: `Section "${sectionKey}" is null` });
        return;
      }

      if (!section.label) {
        errors.push({ section: sectionKey, message: `Section "${sectionKey}" missing label` });
      }

      if (!section.questions || Object.keys(section.questions).length === 0) {
        errors.push({ section: sectionKey, message: `Section "${sectionKey}" has no questions` });
        return;
      }

      if (section.initialQuestion && !section.questions[section.initialQuestion]) {
        errors.push({ section: sectionKey, message: `Initial question "${section.initialQuestion}" not found` });
      }

      // Validate questions
      Object.entries(section.questions).forEach(([qId, question]) => {
        if (!question) return;

        // Check navigation references
        ['onYes', 'onNo', 'next'].forEach(navKey => {
          const ref = question[navKey];
          if (ref && !NAV_TOKENS.includes(ref) && !allQuestionIds.has(ref)) {
            errors.push({
              section: sectionKey,
              question: qId,
              field: navKey,
              message: `"${navKey}" references non-existent "${ref}"`
            });
          }
        });

        // Boolean needs onYes/onNo
        if (question.type === 'boolean') {
          if (!question.onYes) {
            errors.push({ section: sectionKey, question: qId, field: 'onYes', message: `Boolean "${qId}" missing onYes` });
          }
          if (!question.onNo) {
            errors.push({ section: sectionKey, question: qId, field: 'onNo', message: `Boolean "${qId}" missing onNo` });
          }
        }
      });
    });

    return errors;
  }

  /**
   * Build navigation graph for a section showing question hierarchy
   * Returns ordered list of nodes with depth, children, and branch info
   */
  static buildNavigationGraph(section) {
    if (!section?.questions || !section.initialQuestion) {
      return [];
    }

    const questions = section.questions;
    const visited = new Set();
    const nodes = [];

    // Recursive function to traverse the graph
    const traverse = (questionId, depth, branch = null) => {
      // Skip if already visited, is a nav token, or doesn't exist
      if (visited.has(questionId) || NAV_TOKENS.includes(questionId) || !questions[questionId]) {
        // Add terminal node for nav tokens
        if (NAV_TOKENS.includes(questionId)) {
          nodes.push({
            id: questionId,
            depth,
            branch,
            isToken: true,
            type: 'token'
          });
        }
        return;
      }

      visited.add(questionId);
      const question = questions[questionId];

      // Determine children based on question type
      const children = [];
      if (question.onYes) children.push({ id: question.onYes, branch: 'Yes' });
      if (question.onNo) children.push({ id: question.onNo, branch: 'No' });
      if (question.next) children.push({ id: question.next, branch: null });

      nodes.push({
        id: questionId,
        depth,
        branch,
        type: question.type,
        text: question.text,
        children: children.map(c => c.id),
        hasYesNo: question.type === 'boolean' || question.type === 'manager_decision'
      });

      // Traverse children
      children.forEach(child => {
        traverse(child.id, depth + 1, child.branch);
      });
    };

    traverse(section.initialQuestion, 0);
    return nodes;
  }

  /**
   * Get question order based on navigation flow (for display in config admin)
   * Returns flat array of { id, depth, branch } for rendering with indentation
   */
  static getQuestionFlowOrder(section) {
    const graph = this.buildNavigationGraph(section);
    return graph.filter(node => !node.isToken);
  }
}

export default PlannerConfigService;
